import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Typography,
} from "@cointervue/design-system/components";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import type { FormState, JobGeneralInfoSectionProps } from "./types";
import { useCallback } from "react";

const INTERVIEW_DURATIONS = Array.from({ length: 6 }, (_, i) => (i + 1) * 15);
const EXPERIENCE_LEVELS = [
  { value: 0, label: "Fresher" },
  { value: 0.5, label: "6 Months" },
  ...Array.from({ length: 20 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1} ${i + 1 === 1 ? "Year" : "Years"}`,
  })),
];

function JobGeneralInfoSection({ disabled }: JobGeneralInfoSectionProps) {
  const {
    control,
    register,
    getValues,
    clearErrors,
    formState: { errors },
  } = useFormContext<FormState>();

  const validateMinMaxRange = (
    minValue: number | null | undefined,
    maxValue: number | null | undefined,
    fieldName: string,
  ): string | true => {
    if (
      typeof minValue === "number" &&
      typeof maxValue === "number" &&
      minValue > maxValue
    ) {
      return `Min ${fieldName} cannot be greater than max ${fieldName}`;
    }
    return true;
  };

  const minExp = useWatch({ control, name: "minimumExperience" });

  const maxExperienceOptions = EXPERIENCE_LEVELS.filter(
    (level) => typeof minExp !== "number" || level.value >= minExp,
  );

  const minDuration = useWatch({ control, name: "minDuration" });

  const maxDurationOptions = INTERVIEW_DURATIONS.filter(
    (d) => !minDuration || d >= minDuration,
  );
  const handleSelectNumberChange = useCallback(
    (
      onChange: (value: number | null) => void,
      fieldsToClear: Array<keyof FormState>,
    ) =>
      (v: string) => {
        if (v === "") {
          onChange(null);
          clearErrors(fieldsToClear);
          return;
        }

        const num = Number(v);

        if (!Number.isNaN(num) && num >= 0) {
          onChange(num);
          clearErrors(fieldsToClear);
        }
      },
    [clearErrors],
  );

  return (
    <Card className="p-5">
      <CardHeader className="p-0">
        <CardTitle>Basic Job Information</CardTitle>
      </CardHeader>
      <CardContent className="p-0 pt-3">
        <Label className="py-2">Job Title</Label>
        <Input
          placeholder="e.g. Frontend Developer"
          className="bg-brand-gray-light-2"
          disabled={disabled}
          {...register("jobTitle", {
            required: "Job title is required",
          })}
        />
        {errors.jobTitle && (
          <Typography variant="error" className="mt-1">
            {errors.jobTitle.message}
          </Typography>
        )}

        <Label className="pt-5 pb-2">Description</Label>
        <Controller
          name="description"
          control={control}
          rules={{
            required: "Job description is required",
          }}
          render={({ field }) => (
            <Textarea
              {...field}
              value={field.value}
              placeholder="Describe the role and responsibilities..."
              className="bg-brand-gray-light-2 h-30"
              disabled={disabled}
            />
          )}
        />
        {errors.description && (
          <Typography variant="error" className="mt-1">
            {errors.description.message}
          </Typography>
        )}

        <div className="flex flex-col md:flex-row justify-between  md:gap-6">
          <div className=" w-full md:w-1/3">
            <Label className="pt-5 pb-2">Location</Label>
            <Input
              placeholder="e.g. Remote, New York"
              className="bg-brand-gray-light-2"
              disabled={disabled}
              {...register("location", {
                required: "Location is required",
              })}
            />
            {errors.location && (
              <Typography variant="error" className="mt-1">
                {errors.location.message}
              </Typography>
            )}
          </div>

          <div className="flex flex-col w-2/3">
            <Label className="pt-5 pb-2">Experience Level</Label>
            <SelectGroup className="flex gap-6">
              <Controller
                control={control}
                name="minimumExperience"
                rules={{
                  validate: (value) =>
                    validateMinMaxRange(
                      value,
                      getValues("maximumExperience"),
                      "experience",
                    ),
                }}
                render={({ field }) => (
                  <Select
                    disabled={disabled}
                    value={
                      field.value !== null && field.value !== undefined
                        ? String(field.value)
                        : ""
                    }
                    onValueChange={handleSelectNumberChange(field.onChange, [
                      "minimumExperience",
                      "maximumExperience",
                    ])}
                  >
                    <SelectTrigger className="bg-brand-gray-light-2 w-full">
                      <SelectValue placeholder="Select Min Level" />
                    </SelectTrigger>
                    <SelectContent className="max-h-44">
                      {EXPERIENCE_LEVELS.map(({ value, label }) => (
                        <SelectItem key={value} value={String(value)}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />

              <Controller
                control={control}
                name="maximumExperience"
                render={({ field }) => (
                  <Select
                    disabled={disabled}
                    value={
                      field.value !== null && field.value !== undefined
                        ? String(field.value)
                        : ""
                    }
                    onValueChange={handleSelectNumberChange(field.onChange, [
                      "minimumExperience",
                      "maximumExperience",
                    ])}
                  >
                    <SelectTrigger className="bg-brand-gray-light-2">
                      <SelectValue placeholder="Select Max Level" />
                    </SelectTrigger>
                    <SelectContent className="max-h-44 data-[state=open]:slide-in-from-bottom-8 data-[state=open]:zoom-in-100 duration-400">
                      {maxExperienceOptions.map(({ value, label }) => (
                        <SelectItem key={value} value={String(value)}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </SelectGroup>
            {errors.minimumExperience && (
              <Typography variant="error">
                {errors.minimumExperience.message}
              </Typography>
            )}
          </div>
        </div>

        {/* Interview Duration */}
        <div className="flex flex-col w-2/3">
          <Label className="pt-5 pb-2">Interview Duration (Minutes)</Label>

          <SelectGroup className="flex gap-6">
            <Controller
              control={control}
              name="minDuration"
              rules={{
                validate: (value) =>
                  validateMinMaxRange(
                    value,
                    getValues("maxDuration"),
                    "duration",
                  ),
              }}
              render={({ field }) => (
                <Select
                  disabled={disabled}
                  value={field.value !== null ? String(field.value) : ""}
                  onValueChange={handleSelectNumberChange(field.onChange, [
                    "minDuration",
                    "maxDuration",
                  ])}
                >
                  <SelectTrigger className="bg-brand-gray-light-2 w-full">
                    <SelectValue placeholder="Min Duration" />
                  </SelectTrigger>
                  <SelectContent className="max-h-44">
                    {INTERVIEW_DURATIONS.map((d) => (
                      <SelectItem key={d} value={String(d)}>
                        {d} Minutes
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />

            <Controller
              control={control}
              name="maxDuration"
              render={({ field }) => (
                <Select
                  disabled={disabled}
                  value={field.value !== null ? String(field.value) : ""}
                  onValueChange={handleSelectNumberChange(field.onChange, [
                    "minDuration",
                    "maxDuration",
                  ])}
                >
                  <SelectTrigger className="bg-brand-gray-light-2">
                    <SelectValue placeholder="Max Duration" />
                  </SelectTrigger>
                  <SelectContent className="max-h-44">
                    {maxDurationOptions.map((d) => (
                      <SelectItem key={d} value={String(d)}>
                        {d} Minutes
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </SelectGroup>

          {errors.minDuration && (
            <Typography variant="error">
              {errors.minDuration.message}
            </Typography>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default JobGeneralInfoSection;
